import React, { useState, useCallback } from "react";

// ============================================
// TYPES
// ============================================

export interface CallPhase {
  key: "open" | "clinical" | "clarify" | "confirm";
  label: string;
  heading?: string;
  bullets: string[];
  raw?: string;
}

export interface CallScriptsCardProps {
  content: string;
  tip?: string;
  theAsk?: string;
  callMode?: boolean;
  defaultCollapsed?: boolean;
  inContainer?: boolean;
}

const DEFAULT_VISIBLE_BULLETS = 5;

// ============================================
// PHASE CONFIGURATION
// Aligned to accountability-based prompt:
//   OPEN → CLINICAL PICTURE → CLARIFY → CONFIRM
// ============================================

const PHASE_CONFIG: Record<
  CallPhase["key"],
  {
    step: number; label: string; goal: string;
    accentBg: string; accentText: string; accentBorder: string; accentRing: string;
    dotColor: string; stepBg: string; stepText: string;
    accentBgDark: string; accentTextDark: string; accentBorderDark: string; accentRingDark: string;
    dotColorDark: string; stepBgDark: string; stepTextDark: string;
    accentGlow: string;
  }
> = {
  open: {
    step: 1, label: "Open", goal: "Frame the clinical scenario in ≤30 seconds",
    accentBg: "bg-teal-50", accentText: "text-teal-700", accentBorder: "border-teal-200",
    accentRing: "ring-teal-300/40", dotColor: "bg-teal-500/50", stepBg: "bg-teal-100",
    stepText: "text-teal-600", accentBgDark: "bg-teal-950/40", accentTextDark: "text-teal-300",
    accentBorderDark: "border-teal-700/50", accentRingDark: "ring-teal-500/20",
    dotColorDark: "bg-teal-400/50", stepBgDark: "bg-teal-900/40", stepTextDark: "text-teal-300",
    accentGlow: "shadow-teal-500/10",
  },
  clinical: {
    step: 2, label: "Clinical Picture", goal: "Present objective findings without arguing",
    accentBg: "bg-teal-50", accentText: "text-teal-700", accentBorder: "border-teal-200",
    accentRing: "ring-teal-300/40", dotColor: "bg-teal-500/50", stepBg: "bg-teal-100",
    stepText: "text-teal-600", accentBgDark: "bg-teal-950/40", accentTextDark: "text-teal-300",
    accentBorderDark: "border-teal-700/50", accentRingDark: "ring-teal-500/20",
    dotColorDark: "bg-teal-400/50", stepBgDark: "bg-teal-900/40", stepTextDark: "text-teal-300",
    accentGlow: "shadow-teal-500/10",
  },
  clarify: {
    step: 3, label: "Clarify", goal: "Surface the alternative management plan",
    accentBg: "bg-teal-50", accentText: "text-teal-700", accentBorder: "border-teal-200",
    accentRing: "ring-teal-300/40", dotColor: "bg-teal-500/50", stepBg: "bg-teal-100",
    stepText: "text-teal-600", accentBgDark: "bg-teal-950/40", accentTextDark: "text-teal-300",
    accentBorderDark: "border-teal-700/50", accentRingDark: "ring-teal-500/20",
    dotColorDark: "bg-teal-400/50", stepBgDark: "bg-teal-900/40", stepTextDark: "text-teal-300",
    accentGlow: "shadow-teal-500/10",
  },
  confirm: {
    step: 4, label: "Confirm", goal: "Document the outcome and next steps",
    accentBg: "bg-teal-50", accentText: "text-teal-700", accentBorder: "border-teal-200",
    accentRing: "ring-teal-300/40", dotColor: "bg-teal-500/50", stepBg: "bg-teal-100",
    stepText: "text-teal-600", accentBgDark: "bg-teal-950/40", accentTextDark: "text-teal-300",
    accentBorderDark: "border-teal-700/50", accentRingDark: "ring-teal-500/20",
    dotColorDark: "bg-teal-400/50", stepBgDark: "bg-teal-900/40", stepTextDark: "text-teal-300",
    accentGlow: "shadow-teal-500/10",
  },
};

const PHASE_ORDER: CallPhase["key"][] = ["open", "clinical", "clarify", "confirm"];

// ============================================
// INLINE BOLD RENDERER
// ============================================

function renderInlineBold(text: string, callMode: boolean): React.ReactNode {
  if (!text.includes("**")) return text;
  const parts: React.ReactNode[] = [];
  const regex = /\*\*(.+?)\*\*/g;
  let lastIndex = 0;
  let match;
  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) parts.push(text.slice(lastIndex, match.index));
    parts.push(
      <strong key={match.index} className={callMode ? "text-slate-100 font-semibold" : "text-slate-800 font-semibold"}>
        {match[1]}
      </strong>
    );
    lastIndex = match.index + match[0].length;
  }
  if (lastIndex < text.length) parts.push(text.slice(lastIndex));
  return <>{parts}</>;
}

// ============================================
// CONTENT PARSER
// ============================================

function parseCallScripts(content: string): { phases: CallPhase[]; tip?: string } {
  if (!content?.trim()) return { phases: [] };

  let tip: string | undefined;
  const tipMatch = content.match(
    /(?:^|\n)\s*(?:\*{0,2})?(?:Tip|TIP|Strategy|STRATEGY)[:\s]*\*{0,2}\s*(.+?)(?:\n|$)/i
  );
  if (tipMatch) {
    tip = tipMatch[1].replace(/\*+/g, "").replace(/^[:\s]+/, "").trim();
  }

  const lines = content.split("\n");
  const phaseBlocks: { key: CallPhase["key"]; lines: string[] }[] = [];
  let currentPhase: CallPhase["key"] | null = null;
  let currentLines: string[] = [];

  for (const line of lines) {
    const trimmed = line.trim();
    if (tipMatch && trimmed.toLowerCase().includes("tip") && trimmed.includes(tipMatch[1].substring(0, 20))) continue;

    const phaseKey = detectPhaseFromLine(trimmed);
    if (phaseKey) {
      if (currentPhase) phaseBlocks.push({ key: currentPhase, lines: [...currentLines] });
      currentPhase = phaseKey;
      currentLines = [];
      continue;
    }
    if (currentPhase) currentLines.push(line);
  }
  if (currentPhase) phaseBlocks.push({ key: currentPhase, lines: [...currentLines] });

  // Fallback: split by double newlines
  if (phaseBlocks.length === 0) {
    const chunks = content.split(/\n\s*\n/).map((c) => c.trim()).filter(Boolean);
    if (chunks.length >= 4) {
      PHASE_ORDER.forEach((key, i) => { if (chunks[i]) phaseBlocks.push({ key, lines: chunks[i].split("\n") }); });
    } else {
      phaseBlocks.push({ key: "open", lines: content.split("\n") });
    }
  }

  const phases: CallPhase[] = phaseBlocks.map(({ key, lines: blockLines }) => {
    const { heading, bullets, raw } = parsePhaseBlock(blockLines);
    return { key, label: PHASE_CONFIG[key].label, heading: heading || undefined, bullets, raw: raw || undefined };
  });

  return { phases, tip };
}

/**
 * Detect which phase a header line belongs to.
 *
 * Matches the CURRENT prompt format AND legacy format:
 *
 * Current (accountability-based):
 *   OPEN:, CLINICAL PICTURE:, CLARIFY:, CONFIRM:
 *
 * Legacy:
 *   Opening, Necessity / Medical Necessity, Criteria / Coverage Criteria, Close / Closing
 */
function detectPhaseFromLine(line: string): CallPhase["key"] | null {
  const cleaned = line
    .replace(/^#{1,3}\s*/, "")
    .replace(/\*{1,2}/g, "")
    .replace(/^\d+\.\s*/, "")
    .replace(/^(?:phase|step)\s*\d+\s*[:\-\u2013\u2014]\s*/i, "")
    .replace(/[:\s\-\u2013\u2014]*$/, "")
    .toLowerCase()
    .trim();

  if (!cleaned) return null;

  // Phase 1: OPEN / Opening
  if (/^open(ing)?(\s|$)/.test(cleaned)) return "open";

  // Phase 2: CLINICAL PICTURE / Clinical / Necessity / Medical Necessity
  if (/^clinical(\s+picture)?(\s|$)/.test(cleaned)) return "clinical";
  if (/^(medical\s+)?necessity(\s|$)/.test(cleaned)) return "clinical";

  // Phase 3: CLARIFY / Clarification / Criteria / Coverage Criteria
  if (/^clarif(y|ication)(\s|$)/.test(cleaned)) return "clarify";
  if (/^(coverage\s+)?criteria(\s|$)/.test(cleaned)) return "clarify";

  // Phase 4: CONFIRM / Confirmation / Close / Closing
  if (/^confirm(ation)?(\s|$)/.test(cleaned)) return "confirm";
  if (/^clos(e|ing)(\s|$)/.test(cleaned)) return "confirm";

  return null;
}

function parsePhaseBlock(lines: string[]): { heading?: string; bullets: string[]; raw?: string } {
  const nonEmpty = lines.filter((l) => l.trim());
  if (nonEmpty.length === 0) return { bullets: [] };

  const bullets: string[] = [];
  let heading: string | undefined;
  const rawParts: string[] = [];

  for (const line of nonEmpty) {
    const trimmed = line.trim();
    if (/^[•\-\*]\s/.test(trimmed)) {
      bullets.push(trimmed.replace(/^[•\-\*]\s*/, "").trim());
    } else if (/^\*{2}.+\*{2}[:\s]*$/.test(trimmed) && !heading) {
      heading = trimmed.replace(/\*+/g, "").replace(/[:\s]*$/, "").trim();
    } else if (trimmed.endsWith(":") && !heading && trimmed.length < 60) {
      heading = trimmed.replace(/:$/, "").replace(/\*+/g, "").trim();
    } else {
      rawParts.push(trimmed);
    }
  }

  if (bullets.length === 0 && rawParts.length > 0) {
    if (!heading && rawParts.length > 1) heading = rawParts.shift();
  }

  return { heading, bullets, raw: rawParts.join(" ").trim() || undefined };
}

// ============================================
// ICONS
// ============================================

function IconChat({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.8} stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M7.5 8.25h9m-9 3H12m-9.75 1.51c0 1.6 1.123 2.994 2.707 3.227 1.129.166 2.27.293 3.423.379.35.026.67.21.865.501L12 21l2.755-4.133a1.14 1.14 0 01.865-.501 48.172 48.172 0 003.423-.379c1.584-.233 2.707-1.626 2.707-3.228V6.741c0-1.602-1.123-2.995-2.707-3.228A48.394 48.394 0 0012 3c-2.392 0-4.744.175-7.043.513C3.373 3.746 2.25 5.14 2.25 6.741v6.018z" />
    </svg>
  );
}

function IconCopy({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M15.666 3.888A2.25 2.25 0 0013.5 2.25h-3c-1.03 0-1.9.693-2.166 1.638m7.332 0c.055.194.084.4.084.612v0a.75.75 0 01-.75.75H9a.75.75 0 01-.75-.75v0c0-.212.03-.418.084-.612m7.332 0c.646.049 1.288.11 1.927.184 1.1.128 1.907 1.077 1.907 2.185V19.5a2.25 2.25 0 01-2.25 2.25H6.75A2.25 2.25 0 014.5 19.5V6.257c0-1.108.806-2.057 1.907-2.185a48.208 48.208 0 011.927-.184" />
    </svg>
  );
}

function IconCheck({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
    </svg>
  );
}

function IconChevron({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
    </svg>
  );
}

// ============================================
// PHASE CARD
// ============================================

function PhaseCard({ phase, callMode, isActive, onActivate }: {
  phase: CallPhase; callMode: boolean; isActive: boolean; onActivate: () => void;
}) {
  const [copied, setCopied] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const config = PHASE_CONFIG[phase.key];
  const hasContent = phase.bullets.length > 0 || phase.raw;
  const needsCollapse = phase.bullets.length > DEFAULT_VISIBLE_BULLETS;
  const visibleBullets = needsCollapse && !expanded ? phase.bullets.slice(0, DEFAULT_VISIBLE_BULLETS) : phase.bullets;
  const hiddenCount = phase.bullets.length - DEFAULT_VISIBLE_BULLETS;

  const copyPhase = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    const text = [config.label.toUpperCase(), phase.heading || config.goal, ...phase.bullets.map((b) => `• ${b.replace(/\*\*/g, "")}`), phase.raw || ""].filter(Boolean).join("\n");
    navigator.clipboard.writeText(text).then(() => { setCopied(true); setTimeout(() => setCopied(false), 1500); });
  }, [phase, config]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onActivate(); }
  }, [onActivate]);

  const subtitle = phase.heading || config.goal;

  return (
    <div role="button" tabIndex={0} onClick={onActivate} onKeyDown={handleKeyDown} aria-pressed={isActive}
      aria-label={`Phase ${config.step}: ${config.label} — ${subtitle}`}
      className={`group relative rounded-xl border outline-none transition-all duration-200 ${
        callMode
          ? isActive ? `${config.accentBgDark} ${config.accentBorderDark} shadow-lg ${config.accentGlow} ring-1 ${config.accentRingDark}` : `bg-slate-900/60 border-slate-800/60 hover:border-slate-700 focus-visible:ring-1 focus-visible:ring-slate-600`
          : isActive ? `${config.accentBg} ${config.accentBorder} shadow-md ${config.accentGlow} ring-1 ${config.accentRing}` : `bg-white border-slate-200/80 hover:border-slate-300 hover:shadow-sm focus-visible:ring-2 focus-visible:ring-slate-300`
      }`}
    >
      <div className="flex items-center justify-between px-3.5 pt-3 pb-0">
        <div className="flex items-center gap-2 min-w-0">
          <span className={`inline-flex items-center justify-center rounded-md text-[10px] font-bold leading-none flex-shrink-0 ${callMode ? `${config.stepBgDark} ${config.stepTextDark}` : `${config.stepBg} ${config.stepText}`}`} style={{ width: 20, height: 20 }}>{config.step}</span>
          <span className={`text-[13px] font-bold uppercase tracking-wider leading-none ${callMode ? (isActive ? config.accentTextDark : "text-slate-300") : (isActive ? config.accentText : "text-slate-700")}`}>{config.label}</span>
        </div>
        <button onClick={copyPhase} aria-label={`Copy ${config.label} script`}
          className={`flex items-center gap-1 rounded-md px-1.5 py-1 transition-all duration-150 opacity-0 group-hover:opacity-100 focus-visible:opacity-100 ${isActive ? "opacity-100" : ""} ${callMode ? "hover:bg-slate-700/60 text-slate-500 hover:text-slate-300" : "hover:bg-slate-100 text-slate-400 hover:text-slate-600"}`}>
          {copied ? (<><IconCheck className="w-3 h-3 text-emerald-400" /><span className="text-[10px] font-medium text-emerald-400">Copied</span></>) : (<IconCopy className="w-3 h-3" />)}
        </button>
      </div>

      <div className="px-3.5 pt-1 pb-2">
        <p className={`text-[11px] font-medium leading-tight ${callMode ? "text-slate-600" : "text-slate-400"}`}>{subtitle}</p>
      </div>

      <div className={`px-3.5 pb-3.5 ${callMode ? "border-t border-slate-800/40" : "border-t border-slate-100/80"}`}>
        {visibleBullets.length > 0 && (
          <ul className="pt-2.5 flex flex-col" style={{ gap: 10 }}>
            {visibleBullets.map((bullet, i) => (
              <li key={i} className={`flex gap-2 text-[12.5px] leading-[1.5] ${callMode ? "text-slate-400" : "text-slate-500"}`}>
                <span className={`${callMode ? config.dotColorDark : config.dotColor} rounded-full flex-shrink-0 opacity-60`} style={{ width: 5, height: 5, marginTop: 7 }} />
                <span className="min-w-0">{renderInlineBold(bullet, callMode)}</span>
              </li>
            ))}
          </ul>
        )}
        {needsCollapse && (
          <button onClick={(e) => { e.stopPropagation(); setExpanded(!expanded); }}
            className={`mt-2.5 text-[11px] font-medium transition-colors duration-150 ${callMode ? "text-slate-600 hover:text-slate-400" : "text-slate-400 hover:text-slate-600"}`}>
            {expanded ? "Show less" : `+ ${hiddenCount} more`}
          </button>
        )}
        {phase.bullets.length === 0 && phase.raw && (
          <p className={`text-[12.5px] leading-[1.5] pt-2.5 ${callMode ? "text-slate-400" : "text-slate-500"}`}>{renderInlineBold(phase.raw, callMode)}</p>
        )}
        {!hasContent && (
          <p className={`text-[11.5px] italic pt-2.5 ${callMode ? "text-slate-700" : "text-slate-400"}`}>No content for this phase</p>
        )}
      </div>
    </div>
  );
}

// ============================================
// FLOW INDICATOR
// ============================================

function FlowIndicator({ callMode }: { callMode: boolean }) {
  return (
    <div className="flex items-center justify-center -my-0.5 relative z-10">
      <div className="flex items-center gap-1">
        <div className={`w-4 h-px ${callMode ? "bg-slate-800" : "bg-slate-200"}`} />
        <svg className={`w-2.5 h-2.5 ${callMode ? "text-slate-700" : "text-slate-300"}`} fill="none" viewBox="0 0 24 24" strokeWidth={2.5} stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 13.5L12 21m0 0l-7.5-7.5M12 21V3" />
        </svg>
        <div className={`w-4 h-px ${callMode ? "bg-slate-800" : "bg-slate-200"}`} />
      </div>
    </div>
  );
}

// ============================================
// PROGRESS STEPPER
// ============================================

function ProgressStepper({ phases, activeKey, callMode }: { phases: CallPhase[]; activeKey: CallPhase["key"] | null; callMode: boolean }) {
  if (!activeKey || phases.length < 2) return null;
  const activeIdx = phases.findIndex((p) => p.key === activeKey);
  return (
    <div className="flex items-center gap-1.5">
      <span className={`text-[10px] font-semibold tabular-nums mr-0.5 ${callMode ? "text-slate-500" : "text-slate-400"}`}>{activeIdx + 1}/{phases.length}</span>
      {phases.map((p, i) => {
        const cfg = PHASE_CONFIG[p.key];
        const isCurrent = i === activeIdx;
        return (<div key={p.key} className={`rounded-full transition-all duration-200 ${isCurrent ? `${callMode ? cfg.dotColorDark : cfg.dotColor}` : i < activeIdx ? (callMode ? "bg-slate-600" : "bg-slate-300") : (callMode ? "bg-slate-800" : "bg-slate-200")}`} style={{ height: 5, width: isCurrent ? 18 : 5 }} />);
      })}
    </div>
  );
}

// ============================================
// MAIN COMPONENT
// ============================================

export function CallScriptsCard({ content, tip, theAsk, callMode = false, defaultCollapsed = false, inContainer = false }: CallScriptsCardProps) {
  const [isCollapsed, setIsCollapsed] = useState(defaultCollapsed);
  const [activePhase, setActivePhase] = useState<CallPhase["key"] | null>(null);
  const [askCopied, setAskCopied] = useState(false);

  const { phases, tip: parsedTip } = parseCallScripts(content);
  const displayTip = tip || parsedTip;
  const phaseCount = phases.length;

  const copyAsk = useCallback(async () => {
    if (!theAsk) return;
    try {
      await navigator.clipboard.writeText(theAsk);
      setAskCopied(true);
      setTimeout(() => setAskCopied(false), 1500);
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  }, [theAsk]);

  return (
    <div className={`overflow-hidden transition-shadow duration-200 ${
      inContainer
        ? ""
        : `rounded-2xl ${callMode ? "bg-slate-900 border border-slate-800 shadow-lg shadow-black/25" : "bg-white border border-slate-200 shadow-sm hover:shadow-md"}`
    }`}>
      <button onClick={() => setIsCollapsed(!isCollapsed)}
        className={`w-full flex items-center gap-3 px-5 transition-colors duration-150 cursor-pointer ${
          inContainer ? "py-[18px]" : "py-4"
        } ${callMode ? "hover:bg-slate-800/50" : "hover:bg-slate-50"} ${
          !isCollapsed ? (callMode ? "border-b border-slate-800" : "border-b border-slate-100") : ""
        } ${inContainer ? (callMode ? "border-b border-slate-700/30" : "border-b border-slate-100") : ""}`}
        aria-expanded={!isCollapsed} aria-label="Toggle Call Scripts">
        {inContainer ? (
          <>
            <IconChat className={`w-3.5 h-3.5 flex-shrink-0 ${callMode ? "text-slate-500" : "text-slate-400"} opacity-90`} />
            <span className={`text-sm font-medium text-left flex-1 ${callMode ? "text-slate-200" : "text-slate-700"}`}>
              Call Scripts
            </span>
            <span className={`text-[11px] font-medium px-1.5 py-0.5 rounded ${callMode ? "text-slate-500 bg-slate-800" : "text-slate-400 bg-slate-50"}`}>
              {phaseCount} {phaseCount === 1 ? "phase" : "phases"}
            </span>
            {!isCollapsed && <ProgressStepper phases={phases} activeKey={activePhase} callMode={callMode} />}
            <IconChevron className={`w-3.5 h-3.5 ml-1 flex-shrink-0 transition-transform duration-200 ${callMode ? "text-slate-600" : "text-slate-400"} ${isCollapsed ? "" : "rotate-180"}`} />
          </>
        ) : (
          <>
            <span className={`flex items-center justify-center w-10 h-10 rounded-xl flex-shrink-0 ${callMode ? "bg-teal-500/15 text-teal-400" : "bg-teal-50 text-teal-600"}`}>
              <IconChat className="w-5 h-5" />
            </span>
            <div className="text-left min-w-0 flex-1">
              <h3 className={`text-[15px] font-bold tracking-tight ${callMode ? "text-slate-100" : "text-slate-900"}`}>Call Scripts</h3>
              <p className={`text-[12px] font-medium ${callMode ? "text-slate-500" : "text-slate-400"}`}>{phaseCount} {phaseCount === 1 ? "phase" : "phases"} ready</p>
            </div>
            {!isCollapsed && <ProgressStepper phases={phases} activeKey={activePhase} callMode={callMode} />}
            <IconChevron className={`w-4 h-4 ml-1 flex-shrink-0 transition-transform duration-200 ${callMode ? "text-slate-500" : "text-slate-400"} ${isCollapsed ? "" : "rotate-180"}`} />
          </>
        )}
      </button>

      {!isCollapsed && (
        <div className="px-4 py-4 space-y-3">
          {/* The Ask — copy-pasteable opening statement */}
          {theAsk && (
            <div className={`rounded-lg px-3.5 py-3 ${callMode ? "bg-teal-950/40 border border-teal-700/40" : "bg-teal-50/80 border border-teal-100"}`}>
              <div className="flex items-center justify-between mb-1.5">
                <span className={`text-[9px] font-bold uppercase tracking-widest ${callMode ? "text-teal-400" : "text-teal-600"}`}>
                  The Ask
                </span>
                <button
                  onClick={copyAsk}
                  className={`flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium transition-all ${
                    askCopied
                      ? "bg-emerald-100 text-emerald-700"
                      : callMode
                        ? "text-slate-500 hover:text-teal-300 hover:bg-teal-900/40"
                        : "text-slate-400 hover:text-teal-600 hover:bg-teal-100"
                  }`}
                >
                  {askCopied ? (
                    <><IconCheck className="w-3 h-3" /><span>Copied</span></>
                  ) : (
                    <><IconCopy className="w-3 h-3" /><span>Copy</span></>
                  )}
                </button>
              </div>
              <p className={`text-[13px] leading-relaxed font-medium ${callMode ? "text-teal-200" : "text-teal-900"}`}>
                {theAsk}
              </p>
            </div>
          )}

          {displayTip && (
            <div className={`flex items-center gap-2.5 px-3.5 py-2.5 rounded-lg ${callMode ? "bg-violet-500/10 border border-violet-500/20" : "bg-gradient-to-r from-violet-50 to-indigo-50 border border-violet-100/60"}`}>
              <span className={`text-[9px] font-bold uppercase tracking-widest px-2 py-0.5 rounded flex-shrink-0 ${callMode ? "bg-violet-500/25 text-violet-300" : "bg-violet-600 text-white"}`}>Tip</span>
              <p className={`text-[12.5px] font-medium ${callMode ? "text-violet-300" : "text-violet-900/80"}`}>{displayTip}</p>
            </div>
          )}

          {phases.length > 0 ? (
            <div className="space-y-1.5">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {phases.slice(0, 2).map((phase) => (
                  <PhaseCard key={phase.key} phase={phase} callMode={callMode} isActive={activePhase === phase.key}
                    onActivate={() => setActivePhase(activePhase === phase.key ? null : phase.key)} />
                ))}
              </div>
              {phases.length > 2 && <FlowIndicator callMode={callMode} />}
              {phases.length > 2 && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {phases.slice(2, 4).map((phase) => (
                    <PhaseCard key={phase.key} phase={phase} callMode={callMode} isActive={activePhase === phase.key}
                      onActivate={() => setActivePhase(activePhase === phase.key ? null : phase.key)} />
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div className={`text-center py-8 text-sm ${callMode ? "text-slate-600" : "text-slate-400"}`}>No call scripts available for this case.</div>
          )}

          <p className={`text-[10.5px] text-center pt-0.5 ${callMode ? "text-slate-700" : "text-slate-400/70"}`}>Tap a card to focus · speak naturally</p>
        </div>
      )}
    </div>
  );
}

export { parseCallScripts };